import axios from "axios";

/** Domains that use /appointment-letter and /joining-letter from staff grid */
export const GENERAL_STAFF_LETTER_DOMAINS = ["myskoolveritas", "myskoolskh"];

/** Full-width letterhead for Veritas offer / appointment prints */
export const VERITAS_LETTERHEAD_IMAGE =
  "https://cdn.cloudious.net/file-1790760172192-231502633.jpg";

export function isKentStaffLetterDomain(reportDomain) {
  return reportDomain === "myskoolkentschool";
}

/** Staff grid → Vue ID card print (campus logo + org name from API) */
export function isCampusBrandedStaffIdCardDomain(reportDomain) {
  const domain = normalizeReportDomain(reportDomain);
  return domain === "myskoolkentschool" || domain === "myskoolspell";
}

export function isGeneralStaffLetterDomain(reportDomain) {
  return GENERAL_STAFF_LETTER_DOMAINS.includes(
    normalizeReportDomain(reportDomain)
  );
}

export function normalizeReportDomain(reportDomain) {
  return String(reportDomain || "").replace(/_/g, "").toLowerCase().trim();
}

/** SKH letters: left logo from campus API; Veritas uses fixed letterhead image */
export function usesCampusLogoLetterhead(reportDomain) {
  return normalizeReportDomain(reportDomain) === "myskoolskh";
}

export function defaultStaffLetterheadImage(reportDomain) {
  return usesCampusLogoLetterhead(reportDomain)
    ? ""
    : VERITAS_LETTERHEAD_IMAGE;
}

export function generalStaffLetterRefPrefix(reportDomain) {
  return usesCampusLogoLetterhead(reportDomain) ? "SKH-26-BoD" : "VSS-26-BoD";
}

/** Short name in letter body (e.g. "At Veritas, …" / "as per SKH Rules …") */
export function generalStaffLetterShortName(reportDomain, schoolName) {
  if (usesCampusLogoLetterhead(reportDomain)) {
    return String(schoolName || "SKH").trim() || "SKH";
  }
  return "Veritas";
}

export function isStaffLetterPrintDomain(reportDomain) {
  return (
    isKentStaffLetterDomain(reportDomain) ||
    isGeneralStaffLetterDomain(reportDomain)
  );
}

export function defaultSchoolNameForDomain(reportDomain) {
  return getCampusBrandingDefaults(reportDomain).schoolName;
}

export function getCampusBrandingDefaults(reportDomain) {
  const domain = String(reportDomain || "")
    .replace(/_/g, "")
    .toLowerCase()
    .trim();

  if (domain === "myskoolveritas") {
    return {
      schoolName: "Veritas Schooling System",
      schoolLogo: "",
      lockSchoolName: true,
    };
  }
  if (domain === "myskoolskh") {
    return {
      schoolName: "SKH",
      schoolLogo: "",
      lockSchoolName: true,
    };
  }
  if (domain === "myskoolspell") {
    return {
      schoolName: "Speak & Spell Schooling System",
      schoolLogo: "",
      lockSchoolName: false,
    };
  }
  return {
    schoolName: "Kent Schooling System",
    schoolLogo: "/kennt_logo.png",
    lockSchoolName: false,
  };
}

export function parseStaffIds(query) {
  const raw = query.staffIDs || query.staffID || query.tID || "";
  return String(raw)
    .split(",")
    .map((id) => parseInt(id, 10))
    .filter((id) => !Number.isNaN(id) && id > 0);
}

export function normalizeStaffList(responseData) {
  if (!responseData) return [];
  if (Array.isArray(responseData)) return responseData;
  if (Array.isArray(responseData.data)) return responseData.data;
  if (responseData.data && typeof responseData.data === "object") {
    return [responseData.data];
  }
  if (typeof responseData === "object" && responseData.id) {
    return [responseData];
  }
  return [];
}

export async function loadKentCampusInfo(vm, db, cID, reportDomain = "") {
  const branding = getCampusBrandingDefaults(reportDomain);
  const info = {
    schoolName: branding.schoolName,
    campusName: "",
    campusAddress: "",
    campusPhone: "",
    campusEmail: "",
    schoolLogo: branding.schoolLogo,
  };

  try {
    const response = await axios.get("https://apiv26.myskool.app/api/campuses", {
      params: { db, cId: cID },
      headers: {
        Authorization: `Bearer ${vm.$store.state.userData?.token}`,
        accept: "application/json",
      },
    });

    if (
      response.data &&
      response.data.status === "success" &&
      Array.isArray(response.data.data)
    ) {
      const match = response.data.data.find((c) => Number(c.id) === Number(cID));
      if (match) {
        if (!branding.lockSchoolName) {
          info.schoolName =
            match.organizationName || match.orgName || info.schoolName;
        }
        info.campusName = match.branch || match.name || "";
        info.campusAddress = match.address || "";
        if (!info.campusAddress && match.branch) {
          info.campusAddress = match.branch;
        }
        info.campusPhone =
          match.phone || match.contact || match.mobile || "";
        info.campusEmail = match.email || "";
        if (match.logo) {
          info.schoolLogo = match.logo;
        }
      }
    }
  } catch (e) {
    console.warn("Campus info failed to load", e);
  }

  return info;
}

export async function resolveKentStaffIds(vm, options = {}) {
  const {
    db,
    cID,
    staffIDs = [],
    depID = 0,
    status = "present",
  } = options;

  const explicit = (Array.isArray(staffIDs) ? staffIDs : [])
    .map((id) => parseInt(id, 10))
    .filter((id) => !Number.isNaN(id) && id > 0);
  if (explicit.length) return explicit;

  const token = vm.$store.state.userData?.token;
  const params = new URLSearchParams({
    db,
    cId: String(cID),
    dptID: String(depID ?? 0),
    status: status || "present",
    page: "1",
    pageSize: "10000",
    sortBy: "id",
    sortOrder: "asc",
    depname: "",
    applicantStatus: "",
    appointedFrom: "",
    appointedTo: "",
    leavingFrom: "",
    leavingTo: "",
    designation: "",
  });

  const response = await axios.get(
    `${vm.$store.state.domain}staff/LoadDataPagination?${params.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        accept: "application/json",
      },
    }
  );

  const payload = response.data;
  let rows = [];
  if (Array.isArray(payload?.data?.data)) {
    rows = payload.data.data;
  } else if (Array.isArray(payload?.data)) {
    rows = payload.data;
  } else {
    rows = normalizeStaffList(payload);
  }

  return rows
    .map((row) => parseInt(row?.id, 10))
    .filter((id) => !Number.isNaN(id) && id > 0);
}

export function openKentStaffDocumentRoute(vm, routeName, query) {
  const routeData = vm.$router.resolve({ name: routeName, query });
  window.open(routeData.href, "_blank", "noopener,noreferrer");
}

export function parseKentDepIds(query) {
  const raw = query?.depID ?? query?.depIDs ?? "";
  if (raw === null || raw === undefined || raw === "") return [];
  return String(raw)
    .split(",")
    .map((id) => parseInt(id, 10))
    .filter((id) => !Number.isNaN(id));
}

export async function loadKentStaffByDepartments(
  vm,
  db,
  cID,
  depIDs,
  status = "present"
) {
  const token = vm.$store.state.userData?.token;
  const deps =
    Array.isArray(depIDs) && depIDs.length ? depIDs : [0];
  const byId = new Map();

  for (const dptID of deps) {
    const params = new URLSearchParams({
      db,
      cId: String(cID),
      dptID: String(dptID),
      status: status || "present",
      page: "1",
      pageSize: "10000",
      sortBy: "id",
      sortOrder: "asc",
      depname: "",
      applicantStatus: "",
      appointedFrom: "",
      appointedTo: "",
      leavingFrom: "",
      leavingTo: "",
      designation: "",
    });

    const response = await axios.get(
      `${vm.$store.state.domain}staff/LoadDataPagination?${params.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          accept: "application/json",
        },
      }
    );

    const payload = response.data;
    let rows = [];
    if (Array.isArray(payload?.data?.data)) {
      rows = payload.data.data;
    } else if (Array.isArray(payload?.data)) {
      rows = payload.data;
    } else {
      rows = normalizeStaffList(payload);
    }

    rows.forEach((row) => {
      const id = parseInt(row?.id, 10);
      if (!Number.isNaN(id) && id > 0) byId.set(id, row);
    });
  }

  return [...byId.values()];
}

export async function loadKentStaffRecords(vm, db, cID, staffIDs) {
  const headers = {
    Authorization: `Bearer ${vm.$store.state.userData?.token}`,
    accept: "application/json",
  };

  const response = await axios.get(
    `${vm.$store.state.domain}Staff/LoadDataSelected`,
    {
      params: {
        db,
        cID,
        tIDs: staffIDs.join(","),
      },
      headers,
    }
  );

  const payload = response.data;

  if (payload?.data && Array.isArray(payload.data.data)) {
    return payload.data.data;
  }
  if (payload?.status === "success") {
    return normalizeStaffList(payload.data);
  }
  if (Array.isArray(payload?.data)) {
    return payload.data;
  }
  if (Array.isArray(payload)) {
    return payload;
  }
  return normalizeStaffList(payload);
}

export function staffDisplayName(staff) {
  return (
    staff?.name || staff?.staffName || staff?.fullName || "—"
  ).toString().trim();
}

export function staffFatherName(staff) {
  return (
    staff?.father_name ||
    staff?.fatherName ||
    staff?.father ||
    "—"
  ).toString().trim();
}

export function staffDesignation(staff) {
  return (
    staff?.designation || staff?.position || staff?.jobTitle || "—"
  ).toString().trim();
}

export function staffDepartment(staff, departments = []) {
  if (!staff) return "—";
  if (staff.departmentName) return staff.departmentName;
  if (typeof staff.department === "string") return staff.department;
  if (staff.department?.name) return staff.department.name;
  if (staff.departmentID) {
    const match = departments.find(
      (dep) => Number(dep.id) === Number(staff.departmentID)
    );
    return match?.name || "—";
  }
  return "—";
}

function parseKentDateValue(value) {
  if (value === null || value === undefined || value === "") return null;

  const raw = String(value).trim();
  if (!raw) return null;

  const dateOnlyMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    const date = new Date(Number(year), Number(month) - 1, Number(day));
    if (!Number.isNaN(date.getTime())) return date;
  }

  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

export function formatKentDate(value) {
  const date = parseKentDateValue(value);
  if (!date) return value ? String(value).trim().slice(0, 10) : "—";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

export function formatKentDateShort(value) {
  const date = parseKentDateValue(value);
  if (!date) return value ? String(value).trim().slice(0, 10) : "—";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function staffDateOfBirth(staff) {
  return staffField(
    staff,
    ["date_of_birth", "dob", "dateOfBirth", "birthDate", "DOB"],
    ""
  );
}

export function staffEmail(staff) {
  return staffField(
    staff,
    ["personalEmail", "email", "officialEmail"],
    ""
  );
}

export function staffAddress(staff) {
  return staffField(
    staff,
    [
      "address_current",
      "address_permanent",
      "address",
      "residentialAddress",
      "permanentAddress",
    ],
    ""
  );
}

export function staffField(staff, keys, fallback = "—") {
  for (const key of keys) {
    const value = staff?.[key];
    if (value !== null && value !== undefined && String(value).trim() !== "") {
      return String(value).trim();
    }
  }
  return fallback;
}

export function staffSalary(staff) {
  if (!staff) return null;
  const raw =
    staff.totalSalary ?? staff.basicSalary ?? staff.salary ?? staff.grossSalary;
  if (raw === null || raw === undefined || String(raw).trim() === "") {
    return null;
  }
  const num = Number(String(raw).replace(/,/g, ""));
  return Number.isNaN(num) ? null : num;
}

export function formatKentCurrency(value) {
  if (value === null || value === undefined || value === "") return "";
  const num = Number(String(value).replace(/,/g, ""));
  if (Number.isNaN(num)) return String(value).trim();
  return num.toLocaleString("en-PK", { maximumFractionDigits: 0 });
}

export function staffReportingTo(staff) {
  if (!staff) return "";

  const direct = staffField(
    staff,
    [
      "reportToName",
      "reportingToName",
      "reportingTo",
      "headName",
      "headOfSchool",
    ],
    ""
  );
  if (direct && direct !== "—") return direct;

  const reportTo = staff.reportTo;
  if (Array.isArray(reportTo) && reportTo.length) {
    const names = reportTo
      .map((item) => item?.name || item?.staffName || item?.label || "")
      .filter(Boolean);
    if (names.length) return names.join(", ");
  }
  if (reportTo && typeof reportTo === "object") {
    return (
      reportTo.name || reportTo.staffName || reportTo.label || ""
    ).toString().trim();
  }

  return "";
}
